<template>
  <div class="dropdown multi-select" :class="classes">
    <div class="input-group">
      <input
        name="multi-select"
        type="text"
        class="form-input"
        :value="display"
        @click="toggle"
        readonly
      >
      <span class="input-group-addon c-hand" @click="toggle">
        <fa-icon :icon="['fal', 'chevron-down']" />
      </span>
    </div>
    <ul class="menu" v-show="active" :style="menuStyles" @click.stop>
      <template v-if="showToggleAll">
        <li class="menu-item">
          <label class="form-checkbox">
            <input
              type="checkbox"
              name="check-all"
              :checked="isAllSelected"
              :indeterminate.prop="indeterminate"
              @change="handleCheckAll($event.target.checked)"
            />
            <i class="form-icon" /> Todos
          </label>
        </li>
        <li class="divider" />
      </template>
      <template v-for="(item, i) in normalizedItems">
        <li v-if="item.isDivider" class="divider" :key="`divider-${i}`" />
        <li
          v-else
          class="menu-item"
          @click="select(item)"
          :key="`item-${i}`"
        >
          <div class="menu-item-content">
            <slot name="item" :item="item.rawItem">
              <a href="#" @click.prevent>{{ item.label }}</a>
            </slot>
          </div>
          <div class="menu-item-state">
            <fa-icon v-if="item.selected" :icon="['fal', 'check']" />
          </div>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'Dropdown',
  emits: ['select', 'input'],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    labelKey: {
      type: String,
      default: 'label',
    },
    valueKey: {
      type: String,
      default: 'value',
    },
    direction: {
      type: String,
      enum: ['left', 'right'],
      default: 'left',
    },
    maxHeight: {
      type: Number,
      default: 300,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showToggleAll: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      active: false,
      normalizedItems: [],
      checkAll: false,
    };
  },
  mounted() {
    this.normalizeItems();
  },
  beforeUnmount() {
    this.deactivate();
  },
  watch: {
    value: {
      handler() {
        this.normalizeItems();
      },
      deep: true,
    },
    items: {
      handler() {
        this.normalizeItems();
      },
      deep: true,
    },
  },
  computed: {
    classes() {
      return {
        active: this.active,
        'dropdown-right': this.direction === 'right',
        'dropdown-disabled': this.disabled,
      };
    },
    menuStyles() {
      return {
        maxHeight: `${this.maxHeight}px`,
      };
    },
    selectedItems() {
      return this.normalizedItems.filter(item => item.selected);
    },
    selectedValues() {
      return this.selectedItems.map(item => item.value);
    },
    display() {
      if (this.selectedItems.length === 0) {
        return 'Selecione';
      }
      if (this.selectedItems.length === 1) {
        return this.selectedItems[0].label;
      }
      if (this.selectedItems.length === this.items.length) {
        return 'Todos';
      }

      return `${this.selectedItems.length} selecionados`;
    },
    isAllSelected() {
      return this.selectedItems.length === this.items.length;
    },
    indeterminate() {
      return this.selectedItems.length > 0 && this.selectedItems.length < this.items.length;
    },
  },
  methods: {
    normalizeItems() {
      this.normalizedItems = this.items.map((item) => {
        const isString = typeof item === 'string';
        const value = isString ? item : item[this.valueKey];

        return {
          value,
          label: isString ? item : item[this.labelKey],
          isDivider: isString && item === '-',
          selected: this.value.includes(value),
          rawItem: item,
        };
      });
    },
    handleCheckAll(value) {
      this.normalizedItems.forEach((item) => {
        item.selected = value;
      });
      this.$emit('input', this.selectedValues);
    },
    toggle() {
      if (this.active) {
        this.deactivate();
      } else {
        this.activate();
      }
    },
    activate() {
      if (!this.disabled) {
        this.active = true;
        setTimeout(() => (
          document.addEventListener('click', this.deactivate)
        ), 1);
      }
    },
    deactivate() {
      this.active = false;
      document.removeEventListener('click', this.deactivate);
    },
    select(item) {
      item.selected = !item.selected;
      this.$emit('input', this.selectedValues);
    },
  },
};
</script>

<style lang="scss">
@import 'src/assets/scss/variables';

.multi-select {
  display: block;
  .divider {
    margin: $layout-spacing-sm 0;
  }
  .menu-item {
    display: flex;
    padding: $layout-spacing-sm $layout-spacing;
    white-space: nowrap;
    & + .menu-item {
      margin-top: 0;
    }
    .form-checkbox {
      margin: 0;
    }
    .menu-item-content {
      flex: 1;
    }
    .menu-item-state {
      align-content: center;
      flex: 0 0 $control-icon-size;
      margin-left: $layout-spacing;
      text-align: right;
      svg {
        color: $info-color;
        display: block;
        font-size: $control-icon-size;
      }
    }
  }
}
</style>
